import { cx } from '@vizit/theme';
import { useMemo } from 'react';
import { HeroScoringReadinessBadges } from './hero-scoring-readiness-badges';
import { HeroScoringRowsSection } from './hero-scoring-rows-section';
import { HeroScoringScoreSection } from './hero-scoring-score-section';
import type { HeroScoringPanelProps, HeroScoringSection } from './types';

function renderSection(section: HeroScoringSection) {
  if (section.body.kind === 'rows') {
    return <HeroScoringRowsSection key={section.id} section={section} />;
  }
  return <HeroScoringScoreSection key={section.id} section={section} />;
}

const SHOPPER_CLARITY_SECTION_ID = 'four_ws';

// Presence check, not a score check: the badges show whenever the 4Ws section has rows.
function hasShopperClarityRows(sections: HeroScoringSection[]): boolean {
  const shopperClarity = sections.find((s) => s.id === SHOPPER_CLARITY_SECTION_ID);
  return !!shopperClarity && shopperClarity.body.kind === 'rows' && shopperClarity.body.rows.length > 0;
}

/**
 * Diagnostic hero-scoring panel: renders the overarching readiness badges (when a Shopper Clarity section
 * is present) above the compliance check sections and likert score bars.
 *
 * @param props - See {@link HeroScoringPanelProps}.
 * @returns The rendered scoring panel.
 */
function HeroScoringPanel({ sections, agentReady, mobileReady, className }: HeroScoringPanelProps) {
  const showReadiness = useMemo(() => hasShopperClarityRows(sections), [sections]);

  return (
    <div className={cx('flex flex-col gap-8 bg-primary', className)}>
      {showReadiness && (
        <HeroScoringReadinessBadges agentReady={agentReady === true} mobileReady={mobileReady === true} />
      )}
      <div
        className={cx(
          'flex flex-col gap-10 divide-y divide-gray-utility-200 [&>*:not(:first-child)]:pt-10',
          showReadiness && 'border-gray-utility-200 border-t pt-10',
        )}
      >
        {sections.map(renderSection)}
      </div>
    </div>
  );
}

export { HeroScoringPanel };
